/* eslint-disable max-lines-per-function */
import { createApp, h, type App, ref, type Ref } from 'vue';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { i18n } from '@/infrastructure/locales';
import LoginDialog from '../components/LoginDialog.vue';
import type { LoginDialogOptions, LoginDialogResult } from '../components/LoginDialog.vue';
import { getEurekaLoginSDK, type RegisterMethod } from '../utils';
import { RavenService } from '@telecom/shared/services/raven-service/raven-service';
import * as TrackEvents from '@/constants/track-events';
import { TRACK_EVENT_TYPE } from '@/constants';
import { initCookieService } from '@telecom/shared/utils/init-cookie.ts';

// Re-export types for external use
export type { LoginDialogOptions, LoginDialogResult };

/**
 * 登录对话框默认配置
 */
const LOGIN_DIALOG_CONFIG = {
    DEFAULT_LOGIN_URL: '/login',
};

/**
 * 登录对话框服务接口
 */
export interface LoginDialogService {
    /**
     * 打开登录对话框
     * @param options 对话框配置选项
     * @returns Promise<LoginDialogResult> 对话框结果
     */
    open(options?: LoginDialogOptions): Promise<LoginDialogResult>;
}

const VITE_CDN_BASE_URL = import.meta.env.VITE_CDN_BASE_URL;
/**
 * 登录对话框服务实现 V2（SDK 集成方式）
 * 使用 /login/login.sdk.umd.js 提供的 SDK 能力
 */
@injectable()
export class LoginDialogServiceImpl implements LoginDialogService {
    constructor(
        @inject(RavenService)
        private readonly _ravenService: RavenService,
    ) {}

    /**
     * 打开登录对话框
     * @returns Promise<LoginDialogResult> 对话框结果
     */
    async open(_options?: LoginDialogOptions): Promise<LoginDialogResult> {
        try {
            const registrationSource = initCookieService.getAgentName();
            // 埋点：登录触发
            this._ravenService.track(TrackEvents.TRACK_EVENT_REGISTER_EXPOSURE, {
                eventType: TRACK_EVENT_TYPE.SS_PLG_REGISTER,
                eventDetailStr: {
                    trigger_source: _options?.trigger_source,
                    registrationSource: initCookieService.getAgentName(),
                },
            });
            const { openLoginDialog } = await getEurekaLoginSDK();
            try {
                const rt = await openLoginDialog({
                    withoutClose: _options?.withoutClose,
                    onClickRegister: (method: RegisterMethod)=>{
                        // 原有通用注册埋点（保持不变）
                        this._ravenService.track(TrackEvents.TRACK_EVENT_REGISTER_CLICK, {
                            eventType: TRACK_EVENT_TYPE.SS_PLG_REGISTER,
                            eventDetailStr: {
                                registrationSource,
                                registrationMethod: method,
                            },
                        });
                        // 透传调用方的自定义回调（游客注册墙用于追加游客埋点）
                        _options?.onClickRegister?.(method);
                    },
                });
                console.log('登录成功',rt);
                // 登录成功
                const result: LoginDialogResult = {
                    action: 'submit',
                };
                // 刷新页面
                window.location.reload();
                return result;
            } catch (error: any) {
                if (error.type === 'close') {
                    this._ravenService.track(TrackEvents.TRACK_EVENT_REGISTER_CLOSE, {
                        eventType: TRACK_EVENT_TYPE.SS_PLG_REGISTER,
                        eventDetailStr: {
                            registrationSource,
                        },
                    });
                }
                throw error;
            }
        } catch (error: any) {
            console.error(error);
            if (error.type === 'close') {
                // 用户关闭了弹窗
                return {
                    action: 'close',
                };
            } else {
                // 登录失败 (error.type === 'failed' 或其他错误)
                return {
                    action: 'cancel',
                };
            }
        }
    }
}

// 导出服务 Symbol
export const LoginDialogService = Symbol.for('LoginDialogService');
